/**
 * Sports of other activities (runs, swims, gym) next to rides
 * (docs/ACTIVITIES_PLAN.md §9): icon, i18n key and which stats apply.
 */
import type { ActivitySport, RideListItem } from '../types/api.ts';
import { formatDistanceKm, formatDuration } from './rideFormat.ts';

export const ACTIVITY_SPORTS: readonly ActivitySport[] = [
  'run',
  'walk',
  'hike',
  'swim',
  'strength',
  'yoga',
  'row',
  'ski',
  'other',
];

const ICONS: Record<ActivitySport, string> = {
  run: '\u{1F3C3}',
  walk: '\u{1F6B6}',
  hike: '\u{1F97E}',
  swim: '\u{1F3CA}',
  strength: '\u{1F3CB}\u{FE0F}',
  yoga: '\u{1F9D8}',
  row: '\u{1F6A3}',
  ski: '\u{26F7}\u{FE0F}',
  other: '\u{1F3C5}',
};

/** Sports with no meaningful distance (a gym session reports 0 m or none). */
const NO_DISTANCE: ReadonlySet<ActivitySport> = new Set(['strength', 'yoga']);

/** A non-ride item (run, swim, gym...) rather than a ride. */
export function isActivity(item: Pick<RideListItem, 'kind'>): boolean {
  return item.kind === 'activity';
}

/** The known sport, or ``other`` for a value this build doesn't know. */
export function activitySport(sport: string): ActivitySport {
  return ACTIVITY_SPORTS.find((s) => s === sport) ?? 'other';
}

/** i18n key of the sport's name. */
export function sportNameKey(sport: string): string {
  return `activities.sport.${activitySport(sport)}`;
}

/** Emoji icon of the item: the sport for a non-ride, bike or indoor for a ride. */
export function sportIcon(item: Pick<RideListItem, 'kind' | 'sport' | 'is_indoor'>): string {
  if (isActivity(item)) return ICONS[activitySport(item.sport)];
  return item.is_indoor ? '\u{1F3E0}' : '\u{1F6B4}';
}

/** Whether to show a distance for this activity (none for strength/yoga or when unknown). */
export function showsDistance(item: Pick<RideListItem, 'sport' | 'distance_m'>): boolean {
  return item.distance_m != null && !NO_DISTANCE.has(activitySport(item.sport));
}

/** "Run · 45m · 8.2 km · 150 bpm": what the activity shows in one line. */
export function activityLine(item: RideListItem, sportName: string): string {
  const parts = [sportName, formatDuration(item.duration_sec)];
  if (showsDistance(item)) parts.push(formatDistanceKm(item.distance_m));
  if (item.avg_hr != null) parts.push(`${item.avg_hr} bpm`);
  return parts.join(' · ');
}

/** Display name of a source when the item has no ``attribution`` text. */
const SOURCE_NAMES: Record<string, string> = {
  strava: 'Strava',
  garmin: 'Garmin Connect',
  garmin_connect: 'Garmin Connect',
  intervals_icu: 'intervals.icu',
};

/** i18n key for sources named in the app's language. */
const SOURCE_KEYS: Record<string, string> = {
  apple_health: 'sources.appleHealth',
  health_connect: 'sources.healthConnect',
};

/** Where the activity came from: its attribution, a source name, or a file upload. */
export function activitySource(
  item: Pick<RideListItem, 'attribution' | 'source'>,
  t: (key: string) => string,
): string {
  if (item.attribution) return item.attribution;
  const name = SOURCE_NAMES[item.source];
  if (name) return name;
  return t(SOURCE_KEYS[item.source] ?? 'activities.sourceFile');
}
